/**
 * Order status look — one fixed palette for every restaurant. Statuses are the same platform-wide,
 * so their colours are NOT the brand colour (a purple brand would otherwise paint "Rejected"
 * purple). Each status gets its own colour + icon; the label is translated by the caller.
 */
export interface OrderStatusStyle {
  key: string;
  label: string;
  icon: string;
  /** text / icon colour */
  color: string;
  /** soft tint behind the pill */
  bg: string;
}

const S = (label: string, icon: string, color: string, bg: string) => ({ label, icon, color, bg });

const STYLES: Record<string, Omit<OrderStatusStyle, 'key'>> = {
  pending_payment:  S('Awaiting payment', 'solar:card-bold',               '#B45309', '#FEF3C7'),
  placed:           S('Placed',           'solar:document-text-bold',      '#1D4ED8', '#DBEAFE'),
  accepted:         S('Accepted',         'solar:like-bold',               '#4338CA', '#E0E7FF'),
  preparing:        S('Preparing',        'solar:chef-hat-bold',           '#C2410C', '#FFEDD5'),
  ready:            S('Ready',            'solar:bag-check-bold',          '#0F766E', '#CCFBF1'),
  out_for_delivery: S('Out for delivery', 'solar:scooter-bold',            '#0369A1', '#E0F2FE'),
  delivered:        S('Delivered',        'solar:box-bold',                '#15803D', '#DCFCE7'),
  completed:        S('Completed',        'solar:check-circle-bold',       '#15803D', '#DCFCE7'),
  rejected:         S('Rejected',         'solar:close-circle-bold',       '#B91C1C', '#FEE2E2'),
  cancelled:        S('Cancelled',        'solar:forbidden-circle-bold',   '#4B5563', '#F3F4F6'),
  failed:           S('Failed',           'solar:danger-triangle-bold',    '#B91C1C', '#FEE2E2'),
  refunded:         S('Refunded',         'solar:undo-left-round-bold',    '#6D28D9', '#EDE9FE'),
};

/** API spellings that mean the same thing as one of the keys above. */
const ALIASES: Record<string, string> = {
  canceled: 'cancelled', on_the_way: 'out_for_delivery', picked_up: 'out_for_delivery',
  awaiting_payment: 'pending_payment', unpaid: 'pending_payment', payment_failed: 'failed',
};

/** Statuses after which the order is over (no live tracking). */
export const ORDER_TERMINAL = new Set(['delivered', 'completed', 'rejected', 'cancelled', 'failed', 'refunded']);

/**
 * The status to show. While a delivery order is in the kitchen-side flow, the courier's
 * progress (on the way / delivered) is the more useful thing to tell the customer.
 */
export function effectiveOrderStatus(status: string | null | undefined, deliveryStatus?: string | null): string {
  const s = normalize(status);
  const d = normalize(deliveryStatus);
  if ((d === 'out_for_delivery' || d === 'delivered') && !ORDER_TERMINAL.has(s)) return d;
  return s;
}

export function orderStatusStyle(status: string | null | undefined, deliveryStatus?: string | null): OrderStatusStyle {
  const key = effectiveOrderStatus(status, deliveryStatus);
  const st = STYLES[key];
  if (st) return { key, ...st };
  // Unknown status: neutral grey, readable label ("on_hold" → "On hold").
  const label = key ? key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'Unknown';
  return { key, label, icon: 'solar:info-circle-bold', color: '#4B5563', bg: '#F3F4F6' };
}

function normalize(s: string | null | undefined): string {
  const k = String(s || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return ALIASES[k] || k;
}
